import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash/debounce';

import {
  AutoCompleteLite,
  AutoCompleteLiteProps,
  AutoCompleteOptionType,
  AutoCompleteSingleSelectConfig,
  buildAutoCompleteOption,
  VALUE_IDENTIFIER_SEPARATOR,
} from '@hcs/design-system';
import {
  AddressLookupType,
  AddressReference,
  PlaceDetails,
  PlaceType,
} from '@hcs/types';

import { PLACES_TYPES_DEFAULT } from '../../constants';
import { usePlacesSearch } from '../../hooks';
import { PlacesSearchResult } from '../../types/places-search.types';
import {
  getPlaceTypeInputForSearch,
  isAddressResult,
  isPlaceResult,
} from '../../utils/places.utils';

import styles from './PlacesSearch.module.css';

interface PlacesSearchProps
  extends Omit<
    AutoCompleteLiteProps<string>,
    'dataHcName' | 'config' | 'options' | 'onSelectResult'
  > {
  includeAddresses?: boolean;
  includePlaceTypes?: PlaceType[];
  onSelectAddress: (result: AddressReference | undefined) => void;
  onSelectPlace: (result: PlaceDetails | undefined) => void;
  onSelectOther?: (value: { type: string; id: string }) => void;
  onInputChange?: (value: string) => void;
  onOptionsGenerated?: (results: AutoCompleteOptionType<string>[]) => void;
  minCharsToSearch?: number;
  beforeOptions?: AutoCompleteOptionType<string>[];
  afterOptions?: AutoCompleteOptionType<string>[];
}
const dataHcName = 'places-search';
const PLACE_RESULTS_LIMIT = 5;
const ADDRESS_RESULTS_LIMIT = 5;
const VALUE_TYPE_PLACE = 'place';
const VALUE_TYPE_ADDRESS = 'address';

export const PlacesSearch = forwardRef(
  (
    {
      includeAddresses = true,
      includePlaceTypes = PLACES_TYPES_DEFAULT,
      onInputChange,
      onSelectAddress,
      onSelectPlace,
      onSelectOther,
      onOptionsGenerated,
      placeholder = 'Search for a city, ZIP code or address',
      inputStyle = 'search',
      theme,
      minCharsToSearch = 3,
      beforeOptions,
      afterOptions,
      ...autoCompleteProps
    }: PlacesSearchProps,
    ref: React.Ref<HTMLInputElement | null>,
  ) => {
    const {
      mutate: searchPlaces,
      data: rawResults,
      reset: resetSearch,
      cancel: cancelSearch,
    } = usePlacesSearch();
    const [buildingId, setBuildingId] = useState<string | undefined>();
    const inputRef = useRef<HTMLInputElement>(null);
    useImperativeHandle(ref, () => inputRef.current);

    const { searchResults, options } = useMemo<{
      searchResults: PlacesSearchResult[];
      options: AutoCompleteOptionType<string>[];
    }>(() => {
      const allResults: PlacesSearchResult[] = [];
      const resultOptions: AutoCompleteOptionType<string>[] = [];

      // Add places to results
      if (rawResults?.places && rawResults.places.length > 0) {
        // Only add "City" section header if there will be multiple sections
        if (includeAddresses) {
          // Add "City" header
          resultOptions.push(
            buildAutoCompleteOption({
              label: <div className={styles.SectionHeader}>City</div>,
              value: '',
              searchString: '',
              nonInteractive: true,
            }),
          );
        }
        rawResults.places.slice(0, PLACE_RESULTS_LIMIT).forEach((place) => {
          allResults.push(place);
          resultOptions.push(
            buildAutoCompleteOption({
              label: place.description || '',
              value: place.placeId || '',
              valueIdentifier: VALUE_TYPE_PLACE,
              searchString: place.description || '',
            }),
          );
        });
      }

      // Add addresses to results
      if (
        includeAddresses &&
        rawResults?.addresses &&
        rawResults.addresses.length > 0
      ) {
        // Add "Address" header (with separator if there are places above)
        if (rawResults?.places && rawResults.places.length > 0) {
          // Add separator
          resultOptions.push(
            buildAutoCompleteOption({
              label: <div className={styles.Separator} />,
              value: '',
              searchString: '',
              className: styles.SeparatorOption,
              nonInteractive: true,
            }),
          );
        }

        resultOptions.push(
          buildAutoCompleteOption({
            label: <div className={styles.SectionHeader}>Address</div>,
            value: '',
            searchString: '',
            nonInteractive: true,
          }),
        );

        rawResults.addresses
          .slice(0, ADDRESS_RESULTS_LIMIT)
          .forEach((address, index) => {
            allResults.push(address);
            // Show "See All Units" label only if it's a multi-unit building AND we're not already in building search mode
            const isMultiUnit = address.fields?.isUnit === '1' && !buildingId;
            resultOptions.push(
              buildAutoCompleteOption({
                label: isMultiUnit ? (
                  <div
                    className={styles.MultiUnitHit}
                    data-hc-name={`places-search-multi-unit-hit-${address.hcAddressId}-${index}`}
                  >
                    <div>{address.fields?.fullLine}</div>
                    <div>See All Units</div>
                  </div>
                ) : (
                  address.fields?.fullLine || ''
                ),
                value: String(address.hcAddressId) || '',
                valueIdentifier: VALUE_TYPE_ADDRESS,
                searchString: address.fields?.fullLine || '',
              }),
            );
          });
      }

      return { options: resultOptions, searchResults: allResults };
    }, [
      rawResults?.places,
      rawResults?.addresses,
      includeAddresses,
      buildingId,
    ]);

    const handleSelect: AutoCompleteSingleSelectConfig<string>['onSelect'] = (
      value: string | null,
      manualControls,
    ) => {
      if (!value) {
        return;
      }
      const [type, id] = value.split(VALUE_IDENTIFIER_SEPARATOR);

      if (type === VALUE_TYPE_PLACE) {
        const placeResult = searchResults?.filter(isPlaceResult).find((r) => {
          return 'placeId' in r && 'description' in r && r.placeId === id;
        });
        if (placeResult) {
          onSelectPlace(placeResult);
        }
      } else if (type === VALUE_TYPE_ADDRESS) {
        const addressResult = searchResults
          ?.filter(isAddressResult)
          .find((r) => {
            return (
              'hcAddressId' in r &&
              'fields' in r &&
              r.hcAddressId?.toString() === id
            );
          });

        if (addressResult) {
          // Handle multi-unit buildings - search for units using places search with building ID
          // Only trigger building search if we're not already in building search mode
          if (addressResult.fields?.isUnit === '1' && !buildingId) {
            const newSearchStr = `${
              addressResult.fields?.partialLine ||
              addressResult.fields?.fullLine ||
              ''
            } Apt`;
            manualControls?.setSearch(newSearchStr + ' ');
            setBuildingId(addressResult.fields?.hcBuildingId?.toString());
            // Search for units in the building using places search with buildingId
            searchPlaces({
              placeTermInput: {
                term: newSearchStr,
                hcBuildingId: addressResult.fields?.hcBuildingId,
              },
            });
            inputRef.current?.focus();
            // return early here to avoid potential resetSearch
            return;
          } else {
            // For individual units or non-multi-unit addresses, select and close
            onSelectAddress(addressResult);
          }
        }
      } else if (onSelectOther) {
        // Handle selection of other result items added to the autocomplete by parents (i.e. recent searches)
        onSelectOther({ type, id });
      }

      if (autoCompleteProps.clearAfterSelect) {
        resetSearch();
      }
    };

    const debouncedSearch = useMemo(
      () =>
        debounce((v: string) => {
          searchPlaces({
            placeTermInput: {
              term: v,
              placeType: getPlaceTypeInputForSearch(v, includePlaceTypes),
              ...(buildingId
                ? {
                    hcBuildingId: parseInt(buildingId, 10),
                    addressLookupType: AddressLookupType.BuildingLookup,
                  }
                : {}),
            },
          });
        }, 300),
      [includePlaceTypes, buildingId, searchPlaces],
    );

    useEffect(() => {
      return () => {
        debouncedSearch.cancel();
      };
    }, [debouncedSearch]);

    // Report search results to parent component
    useEffect(() => {
      if (onOptionsGenerated) {
        onOptionsGenerated(options);
      }
    }, [options, onOptionsGenerated]);

    const handleChange = useCallback(
      (v: string) => {
        cancelSearch();
        if (v.length >= minCharsToSearch) {
          debouncedSearch(v);
        } else {
          debouncedSearch.cancel();
          setBuildingId(undefined);
          resetSearch();
        }
        onInputChange?.(v);
      },
      [
        minCharsToSearch,
        debouncedSearch,
        onInputChange,
        resetSearch,
        cancelSearch,
      ],
    );

    return (
      <AutoCompleteLite
        {...autoCompleteProps}
        ref={inputRef}
        inputStyle={inputStyle}
        placeholder={placeholder}
        dataHcName={dataHcName}
        options={[
          ...(beforeOptions ?? []),
          ...(options ?? []),
          ...(afterOptions ?? []),
        ]}
        theme={{
          InputContainer: theme?.InputContainer,
          OptionsContainer: theme?.OptionsContainer,
          Option: theme?.Option,
          SearchInputStyle: theme?.SearchInputStyle,
        }}
        onSelectResult={handleSelect}
        onInputChange={handleChange}
      />
    );
  },
);
